import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  Breadcrumbs,
  type BreadcrumbItem,
} from '../../components/layout/Breadcrumbs';
import {
  deleteMerchantProgram,
  fetchMerchantOptions,
  fetchMerchantPrograms,
  type MerchantOption,
  type MerchantProgramRecord,
  type MerchantProgramStatus,
  type MerchantProgramType,
} from '../../services/merchantProgramsService';

const PAGE_SIZE = 20;

const TYPE_LABELS: Record<MerchantProgramType, string> = {
  fee_waiver: 'Miễn phí',
  fee_schedule: 'Lịch thu phí',
  announcement: 'Khuyến mãi/Thông báo',
  new_merchant_registration: 'Đăng ký merchant mới',
};

const TYPE_COLORS: Record<MerchantProgramType, string> = {
  fee_waiver: 'green',
  fee_schedule: 'gold',
  announcement: 'blue',
  new_merchant_registration: 'purple',
};

const STATUS_LABELS: Record<MerchantProgramStatus, string> = {
  scheduled: 'Lên lịch',
  active: 'Đang chạy',
  ended: 'Đã kết thúc',
  cancelled: 'Đã hủy',
};

const STATUS_COLORS: Record<MerchantProgramStatus, string> = {
  scheduled: 'processing',
  active: 'success',
  ended: 'default',
  cancelled: 'error',
};

function formatFeeConfig(r: MerchantProgramRecord): string {
  if (r.program_type === 'fee_waiver') return 'Miễn phí (0đ)';
  if (r.program_type === 'new_merchant_registration') {
    const cfg = r.config as {
      free_registration_enabled?: boolean;
      packages?: Array<{ name?: string; price?: number }>;
    };
    const count = cfg?.packages?.length ?? 0;
    const free = cfg?.free_registration_enabled ? ' · Miễn phí' : '';
    return count > 0 ? `${count} gói${free}` : '–';
  }
  if (r.program_type === 'fee_schedule') {
    const cfg = r.config as {
      fee_type?: string;
      fee_rate?: number;
      fee_amount_vnd?: number;
    };
    if (cfg?.fee_type === 'fixed') {
      return `${(cfg.fee_amount_vnd ?? 0).toLocaleString('vi-VN')}đ / đơn`;
    }
    return `${((cfg?.fee_rate ?? 0) * 100).toFixed(2)}% / đơn`;
  }
  return '–';
}

function formatPeriod(r: MerchantProgramRecord): string {
  const start = r.starts_at ? dayjs(r.starts_at).format('DD/MM/YY HH:mm') : '–';
  const end = r.ends_at
    ? dayjs(r.ends_at).format('DD/MM/YY HH:mm')
    : 'Không giới hạn';
  return `${start} → ${end}`;
}

export default function MerchantProgramsListPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<MerchantProgramRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState<
    MerchantProgramType | undefined
  >();
  const [statusFilter, setStatusFilter] = useState<
    MerchantProgramStatus | undefined
  >();
  const [restaurantFilter, setRestaurantFilter] = useState<
    number | undefined
  >();
  const [merchants, setMerchants] = useState<MerchantOption[]>([]);

  const loadMerchants = useCallback(async () => {
    try {
      setMerchants(await fetchMerchantOptions());
    } catch {
      /* ignore */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMerchantPrograms({
        page,
        limit: PAGE_SIZE,
        programType: typeFilter,
        status: statusFilter,
        restaurantId: restaurantFilter,
      });
      if (res.success && Array.isArray(res.data)) {
        setRows(res.data);
        setTotal(res.pagination?.total ?? res.data.length);
      } else {
        setRows([]);
        setTotal(0);
        setError(res.message || 'Không tải được danh sách');
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || 'Lỗi tải dữ liệu');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, statusFilter, restaurantFilter]);

  useEffect(() => {
    loadMerchants();
  }, [loadMerchants]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = (record: MerchantProgramRecord) => {
    const scopeLabel =
      record.program_type === 'new_merchant_registration'
        ? 'toàn hệ thống'
        : `quán ${record.restaurant_name ?? `#${record.restaurant_id}`}`;
    Modal.confirm({
      title: 'Xóa chương trình?',
      content: (
        <>
          Xóa <strong>{record.name}</strong> ({scopeLabel})?
        </>
      ),
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const res = await deleteMerchantProgram(record.id);
          if (res.success) {
            message.success('Đã xóa chương trình');
            load();
          } else {
            message.error(res.message || 'Xóa thất bại');
          }
        } catch (e: unknown) {
          const ax = e as { response?: { data?: { message?: string } } };
          message.error(ax.response?.data?.message || 'Xóa thất bại');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Tên chương trình',
      key: 'name',
      render: (_: unknown, r: MerchantProgramRecord) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.name}</div>
          {r.description && (
            <div style={{ fontSize: 12, color: '#666' }}>{r.description}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Quán',
      key: 'restaurant',
      width: 200,
      render: (_: unknown, r: MerchantProgramRecord) => (
        <span>
          <ShopOutlined />{' '}
          {r.program_type === 'new_merchant_registration'
            ? 'Toàn hệ thống'
            : r.restaurant_name ?? `Quán #${r.restaurant_id}`}
        </span>
      ),
    },
    {
      title: 'Loại',
      key: 'type',
      width: 150,
      render: (_: unknown, r: MerchantProgramRecord) => (
        <Tag color={TYPE_COLORS[r.program_type]}>
          {TYPE_LABELS[r.program_type]}
        </Tag>
      ),
    },
    {
      title: 'Phí',
      key: 'fee',
      width: 140,
      render: (_: unknown, r: MerchantProgramRecord) => formatFeeConfig(r),
    },
    {
      title: 'Thời gian',
      key: 'period',
      width: 240,
      render: (_: unknown, r: MerchantProgramRecord) => formatPeriod(r),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 120,
      render: (_: unknown, r: MerchantProgramRecord) => {
        const st = r.derived_status ?? r.status;
        return <Tag color={STATUS_COLORS[st]}>{STATUS_LABELS[st]}</Tag>;
      },
    },
    {
      title: 'Banner',
      key: 'banner',
      width: 90,
      render: (_: unknown, r: MerchantProgramRecord) =>
        r.program_type === 'new_merchant_registration' ? (
          <Tag color="default">N/A</Tag>
        ) : (
          <Tag color={r.show_banner ? 'success' : 'default'}>
            {r.show_banner ? 'Bật' : 'Tắt'}
          </Tag>
        ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 110,
      render: (_: unknown, r: MerchantProgramRecord) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/merchant-programs/${r.id}/edit`)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(r)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumbs
        items={
          [
            { label: 'Trang chủ', path: '/' },
            { label: 'Chương trình Merchant' },
          ] satisfies BreadcrumbItem[]
        }
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <h2 style={{ margin: 0 }}>Chương trình dành riêng cho Merchant</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/merchant-programs/create')}
        >
          Thêm chương trình
        </Button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 16 }}
          closable
        />
      )}

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            placeholder="Loại chương trình"
            allowClear
            style={{ width: 200 }}
            value={typeFilter}
            onChange={v => {
              setTypeFilter(v);
              setPage(1);
            }}
            options={(Object.keys(TYPE_LABELS) as MerchantProgramType[]).map(
              t => ({
                value: t,
                label: TYPE_LABELS[t],
              })
            )}
          />
          <Select
            placeholder="Trạng thái"
            allowClear
            style={{ width: 160 }}
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v);
              setPage(1);
            }}
            options={(
              Object.keys(STATUS_LABELS) as MerchantProgramStatus[]
            ).map(s => ({
              value: s,
              label: STATUS_LABELS[s],
            }))}
          />
          <Select
            placeholder="Lọc theo quán"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 240 }}
            value={restaurantFilter}
            onChange={v => {
              setRestaurantFilter(v);
              setPage(1);
            }}
            options={merchants.map(m => ({
              value: m.restaurantId,
              label: m.label,
            }))}
          />
        </Space>
      </Card>

      <Card>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            onChange: p => setPage(p),
          }}
        />
      </Card>
    </div>
  );
}
